"use client";

import "./globals.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { Suspense, useEffect, useState } from "react";
import { I18nextProvider } from "react-i18next";

import { client } from "@/api/client/client.gen";
import { createClientConfig } from "@/api/hey-api";
import { LanguageInitializer } from "@/components/language-initializer";
import { ServiceWorkerRegistration } from "@/components/ServiceWorkerRegistration";
import Toaster from "@/components/ui/toaster";
import { DEFAULT_LANGUAGE } from "@/constants/language";
import { AuthProvider } from "@/contexts/auth-context";
import { useMounted } from "@/hooks/use-mounted";
import { tokenManager } from "@/lib/token-manager";
import i18n from "@/locales/config";

// Initialize the client configuration
client.setConfig(createClientConfig());

// Inject x-session-id header on every outgoing request for activity logging
client.interceptors.request.use((options) => {
  if (typeof window !== "undefined") {
    const isCallPage = window.location.pathname.includes("/call/");
    const sessionId = isCallPage
      ? null
      : sessionStorage.getItem("dashboard_activity_session_id");
    if (sessionId && options.headers) {
      if (typeof (options.headers as any).set === "function") {
        (options.headers as Headers).set("x-session-id", sessionId);
      } else if (Array.isArray(options.headers)) {
        (options.headers as string[][]).push(["x-session-id", sessionId]);
      } else {
        (options.headers as any)["x-session-id"] = sessionId;
      }
    }
  }
});

client.interceptors.response.use((response) => {
  if (response.status === 401) {
    tokenManager.clearTokens();

    // Only redirect if we're not already on the login page to avoid infinite redirects
    if (
      typeof window !== "undefined" &&
      !window.location.pathname.includes("/login")
    ) {
      window.location.href = "/login";
    }
  }
  return response;
});

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export default function RootLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [i18nInstance, setI18nInstance] = useState(i18n);
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  const [isLoading, setIsLoading] = useState(true);
  const mounted = useMounted();

  // Initialize i18n and handle language changes
  useEffect(() => {
    const init = async () => {
      try {
        // Initialize i18n if not already initialized
        if (!i18n.isInitialized) {
          await i18n.init();
        }

        // Set the current language from i18n
        setCurrentLanguage(i18n.language);
        setI18nInstance(i18n);
      } catch (error) {
        console.error("Failed to initialize i18n:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Initialize i18n
    init();

    // Handle language changes
    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(lng);
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, []);

  // Set the HTML lang attribute based on current language
  useEffect(() => {
    if (!i18nInstance.isInitialized || typeof window === "undefined") return;

    const lang = i18nInstance.language || "en";
    document.documentElement.lang = lang;

    const handleLanguageChange = (lng: string) => {
      document.documentElement.lang = lng;
    };

    i18nInstance.on("languageChanged", handleLanguageChange);

    return () => {
      i18nInstance.off("languageChanged", handleLanguageChange);
    };
  }, [i18nInstance]);

  // Don't render until mounted to prevent hydration mismatch
  if (!mounted || isLoading) {
    return (
      <html lang={DEFAULT_LANGUAGE} suppressHydrationWarning>
        <head>
          <meta
            name="google-site-verification"
            content="tCfzyS8eqDbqsSsRrU1ftn55V_hMU00v5Jl-oLGRgS4"
          />
        </head>
        <body>
          <div className="flex min-h-screen items-center justify-center">
            <div className="border-primary size-12 animate-spin rounded-full border-t-2 border-b-2"></div>
          </div>
        </body>
      </html>
    );
  }

  return (
    <html lang={currentLanguage} suppressHydrationWarning>
      <head>
        <meta
          name="google-site-verification"
          content="tCfzyS8eqDbqsSsRrU1ftn55V_hMU00v5Jl-oLGRgS4"
        />
      </head>
      <body>
        <QueryClientProvider client={queryClient}>
          <I18nextProvider i18n={i18nInstance}>
            <AuthProvider>
              <LanguageInitializer />
              <ServiceWorkerRegistration />
              <Suspense fallback={<div></div>}>{children}</Suspense>
              <Toaster />
            </AuthProvider>
          </I18nextProvider>
        </QueryClientProvider>
      </body>
    </html>
  );
}
